'use client';

import { useEffect, useState } from 'react';
import { ErrorFallback } from '@/components/ErrorFallback';
import { useErrorReport } from '@/hooks/useErrorReport';
import { isChunkLoadError } from '@/lib/errorReportingService';
import { isCapacitor } from '@/lib/platformUtils';
import { clearNativeCache } from '@/lib/nativeCacheBridge';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

/**
 * Route-level error boundary for the root app segment.
 * Next.js automatically renders this when a Server Component or Client
 * Component in the root segment throws.
 *
 * Chunk loading errors (deployment mismatches) are handled specially:
 * the app clears caches and auto-reloads instead of showing an error page.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [isChunkError, setIsChunkError] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);

  const { reportReference, isReporting } = useErrorReport({
    error,
    componentName: 'ErrorPage',
    userAction: 'Page-level error caught by error.tsx',
    // Skip reporting for chunk errors — reportError also filters them,
    // but setting skipReporting avoids the fallback localStorage write too
    skipReporting: isChunkLoadError(error),
  });

  useEffect(() => {
    if (!isChunkLoadError(error)) return;

    setIsChunkError(true);
    setIsRecovering(true);

    // Clear caches and reload to get fresh chunks from the new deployment
    const recover = async () => {
      try {
        // Ask service worker to clear caches
        if (navigator.serviceWorker?.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHES' });
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // Clear browser cache storage directly
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames
              .filter(name =>
                name.includes('nextjs') ||
                name.includes('totalaccess') ||
                name.includes('pages') ||
                name.includes('navigation') ||
                name.includes('chunks')
              )
              .map(name => caches.delete(name))
          );
        }

        // Clear native WebView disk cache (Capacitor only)
        if (isCapacitor()) {
          clearNativeCache();
        }

        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (e) {
        console.error('[ErrorPage] Cache clear failed during chunk error recovery:', e);
      } finally {
        // Reload to get fresh chunks
        window.location.reload();
      }
    };

    void recover();
  }, [error]);

  // For chunk errors, show a branded "Updating..." UI while recovering
  if (isChunkError || isRecovering) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white dark:bg-gray-900">
        <BrandedLoadingSpinner
          size="lg"
          message="Updating App..."
          subMessage="A new version is available. Clearing cache and reloading..."
          showSurface
          showProgressBar
          suppressClearCacheButton
        />
      </div>
    );
  }

  return (
    <ErrorFallback
      error={error}
      reportReference={reportReference}
      isReporting={isReporting}
      onReset={reset}
    />
  );
}
